/**
 * Bare cookie-refresh helper.
 *
 * Used by both `SessionBootstrap` (on app start) and the main API
 * client's 401 interceptor (on a stale-access-token retry). It is
 * deliberately built on a STANDALONE axios instance with no
 * interceptors and no default `Authorization` header so that:
 *
 *   1. The main client's 401 interceptor cannot recurse into itself
 *      via the refresh request.
 *   2. A stale `Authorization: Bearer <expired access token>` header
 *      cannot end up on a refresh request that should rely solely on
 *      the HttpOnly `invento_refresh` cookie.
 *
 * Request body is sent as `undefined` — there is no body refresh-token
 * field after Slice 2.6a. The cookie is the only credential the
 * backend accepts; if a body `refreshToken` were sent it would be
 * ignored server-side and a single warn line would be logged.
 *
 * Response shape after Slice 2.6a:
 *   `{ success: true, data: { tokens: { accessToken } } }` — the
 *   helper returns only the access token. There is no `refreshToken`
 *   field in the body and no parser for one.
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const refreshHttp = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export interface RefreshResult {
  accessToken: string;
}

export async function refreshAccessToken(): Promise<RefreshResult> {
  const response = await refreshHttp.post('/auth/refresh', undefined);
  const accessToken = response?.data?.data?.tokens?.accessToken;
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new Error('Refresh response missing access token');
  }
  return { accessToken };
}

// Test-only seam — lets unit tests bind axios-mock-adapter to the
// internal instance without exposing it as a regular runtime export.
export const __refreshHttpForTests = refreshHttp;
